import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Flame, Phone, MapPin, Mail, BookOpen, ChevronDown, Moon, Sun } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';

const FacebookIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
  </svg>
);

const TikTokIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const productCategories = [
  { id: 'peleti', name: 'Peleti', desc: 'Peleti din lemn de inalta calitate' },
  { id: 'brichete', name: 'Brichete', desc: 'Brichete pentru incalzire eficienta' },
  { id: 'carbuni', name: 'Carbuni', desc: 'Carbuni pentru soba si gratar' },
  { id: 'lemne', name: 'Lemne de foc', desc: 'Lemne uscate de esenta tare' },
  { id: 'carbuni_gratar', name: 'Carbuni de Gratar', desc: 'Ideal pentru gratar si BBQ' },
];

export default function Navbar() {
  const { totalItems, setIsOpen } = useCart();
  const { dark, toggle } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Prevent body scroll when mobile menu is open - uses class to avoid forced reflow
  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', mobileOpen);
    return () => { document.body.classList.remove('overflow-hidden'); };
  }, [mobileOpen]);

  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) return null;

  const links = [
    { href: '/', label: 'Acasa' },
    { href: '/magazin', label: 'Magazin', hasDropdown: true },
    { href: '/despre-noi', label: 'Despre Noi' },
    { href: '/stiai-ca', label: 'Stiai ca...' },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Top Bar - Desktop */}
      <div className="hidden md:block bg-white dark:bg-stone-900 border-b border-stone-100 dark:border-stone-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-10 text-sm">
            <div className="flex items-center gap-6">
              <a href="https://maps.google.com/?q=Str.+Principal%C4%83+nr.12,+Sendreni,+Gala%C8%9Bi" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-stone-700 dark:text-stone-400 hover:text-orange-600 transition-colors">
                <MapPin className="h-3.5 w-3.5" />
                <span>Str. Principala nr.12, Sendreni, Galati</span>
              </a>
              <a href="mailto:contact@peletigalati.ro" className="flex items-center gap-2 text-stone-700 dark:text-stone-400 hover:text-orange-600 transition-colors">
                <Mail className="h-3.5 w-3.5" />
                <span>contact@peletigalati.ro</span>
              </a>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/stiai-ca" className="flex items-center gap-2 text-stone-700 dark:text-stone-400 hover:text-orange-600 transition-colors">
                <BookOpen className="h-3.5 w-3.5" />
                <span>Blog</span>
              </Link>
              <div className="flex items-center gap-1">
                <a href="https://www.facebook.com/peletigalati/" target="_blank" rel="noopener noreferrer" className="text-stone-600 dark:text-stone-400 hover:text-orange-600 transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Facebook">
                  <FacebookIcon className="h-5 w-5" />
                </a>
                <a href="https://www.tiktok.com/@peletigalati.ro" target="_blank" rel="noopener noreferrer" className="text-stone-600 dark:text-stone-400 hover:text-orange-600 transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="TikTok">
                  <TikTokIcon className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Bar - Mobile */}
      <div className="md:hidden bg-white dark:bg-stone-900 border-b border-stone-100 dark:border-stone-800">
        <div className="px-4">
          <div className="flex items-center justify-between h-9 text-xs">
            <a href="https://maps.google.com/?q=Str.+Principal%C4%83+nr.12,+Sendreni,+Gala%C8%9Bi" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-stone-500">
              <MapPin className="h-3 w-3" />
              <span>Sendreni, Galati</span>
            </a>
            <div className="flex items-center gap-4">
              <a href="mailto:contact@peletigalati.ro" className="flex items-center gap-1.5 text-stone-500">
                <Mail className="h-3 w-3" />
                <span>Email</span>
              </a>
              <div className="flex items-center">
                <a href="https://www.facebook.com/peletigalati/" target="_blank" rel="noopener noreferrer" className="text-stone-600 dark:text-stone-400 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Facebook">
                  <FacebookIcon className="h-4 w-4" />
                </a>
                <a href="https://www.tiktok.com/@peletigalati.ro" target="_blank" rel="noopener noreferrer" className="text-stone-600 dark:text-stone-400 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="TikTok">
                  <TikTokIcon className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="sticky top-0 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md border-b border-stone-100 dark:border-stone-800 z-40" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
              <Flame className="h-7 w-7 text-orange-600" />
              <span className="font-heading text-2xl font-bold text-stone-900 dark:text-white uppercase tracking-wide">
                Peleti<span className="text-orange-600">Galati</span>
              </span>
            </Link>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center gap-8">
              {links.map(link => (
                link.hasDropdown ? (
                  <div key={link.href} className="relative" onMouseEnter={() => setShopDropdownOpen(true)} onMouseLeave={() => setShopDropdownOpen(false)}>
                    <Link
                      to={link.href}
                      className={`font-heading text-lg uppercase tracking-wider transition-colors hover:text-orange-600 flex items-center gap-1 ${
                        location.pathname === link.href || location.pathname.startsWith('/produs') ? 'text-orange-600 font-semibold' : 'text-stone-800 dark:text-stone-300'
                      }`}
                      data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
                    >
                      {link.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${shopDropdownOpen ? 'rotate-180' : ''}`} />
                    </Link>
                    {shopDropdownOpen && (
                      <div className="absolute top-full left-0 pt-2 w-64 z-50">
                        <div className="bg-white dark:bg-stone-800 rounded-xl shadow-xl border border-stone-100 dark:border-stone-700 overflow-hidden">
                          <div className="p-2">
                            {productCategories.map((cat) => (
                              <Link key={cat.id} to={`/magazin?category=${cat.id}`} className="block px-4 py-3 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors group" onClick={() => setShopDropdownOpen(false)}>
                                <span className="font-heading text-base font-semibold text-stone-900 dark:text-white group-hover:text-orange-600 transition-colors">{cat.name}</span>
                                <p className="text-xs text-stone-500 mt-0.5">{cat.desc}</p>
                              </Link>
                            ))}
                          </div>
                          <div className="border-t border-stone-100 p-2">
                            <Link to="/magazin" className="block px-4 py-2 text-center text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors" onClick={() => setShopDropdownOpen(false)}>
                              Vezi toate produsele
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`font-heading text-lg uppercase tracking-wider transition-colors hover:text-orange-600 ${
                      location.pathname === link.href ? 'text-orange-600 font-semibold' : 'text-stone-800 dark:text-stone-300'
                    }`}
                    data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <button onClick={toggle} className="p-2.5 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors rounded-xl" data-testid="theme-toggle" aria-label={dark ? 'Tema deschisa' : 'Tema intunecata'}>
                {dark ? <Sun className="h-5 w-5 text-amber-400" /> : <Moon className="h-5 w-5 text-stone-600" />}
              </button>

              <a href="tel:+40748035510" className="hidden md:flex items-center gap-2 px-5 py-2 bg-stone-900 dark:bg-orange-600 text-white text-sm font-medium rounded-full hover:bg-orange-600 dark:hover:bg-orange-500 transition-colors" data-testid="navbar-phone">
                <Phone className="h-4 w-4" />
                Suna acum
              </a>

              <button onClick={() => setIsOpen(true)} className="relative p-2.5 hover:bg-stone-50 transition-colors rounded-xl" data-testid="navbar-cart-btn" aria-label="Cos cumparaturi">
                <ShoppingCart className="h-6 w-6 text-stone-800 dark:text-stone-200" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-orange-600 text-white text-xs border-0 rounded-full font-semibold" data-testid="cart-badge">
                    {totalItems}
                  </span>
                )}
              </button>

              {/* Mobile menu button */}
              <button className="md:hidden p-2 hover:bg-stone-50 dark:hover:bg-stone-700 rounded-xl" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Meniu navigare">
                <Menu className="h-6 w-6 text-stone-800 dark:text-stone-200" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* CSS-only Mobile Menu (no Radix Dialog) */}
      <div
        className={`fixed inset-0 z-50 transition-visibility ${mobileOpen ? 'visible' : 'invisible pointer-events-none'}`}
        aria-hidden={!mobileOpen}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${mobileOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setMobileOpen(false)}
        />
        {/* Sidebar */}
        <div className={`absolute right-0 top-0 bottom-0 w-72 bg-white dark:bg-stone-900 shadow-2xl transition-transform duration-300 ease-out ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <div className="font-heading text-2xl uppercase flex items-center gap-2">
                <Flame className="h-6 w-6 text-orange-600" />
                <span className="text-stone-900 dark:text-white font-bold">Peleti<span className="text-orange-600">Galati</span></span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-2 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-xl" aria-label="Inchide meniul">
                <X className="h-5 w-5 text-stone-600 dark:text-stone-300" />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              {links.map(link => (
                <div key={link.href}>
                  <Link
                    to={link.href}
                    onClick={() => { if (!link.hasDropdown) setMobileOpen(false); }}
                    className={`font-heading text-xl uppercase tracking-wider px-4 py-3 rounded-lg transition-colors hover:bg-orange-50 dark:hover:bg-stone-800 hover:text-orange-600 block ${
                      location.pathname === link.href ? 'text-orange-600 bg-orange-50 dark:bg-stone-800 font-semibold' : 'text-stone-600 dark:text-stone-300'
                    }`}
                    data-testid={`mobile-nav-${link.href.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                  {link.hasDropdown && (
                    <div className="ml-4 mt-1 space-y-1">
                      {productCategories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/magazin?category=${cat.id}`}
                          onClick={() => setMobileOpen(false)}
                          className="block px-4 py-2.5 text-sm text-stone-500 dark:text-stone-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-stone-800 rounded-lg transition-colors"
                          data-testid={`mobile-cat-${cat.id}`}
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href="tel:+40748035510"
                className="mx-4 mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-orange-700 text-white font-medium rounded-xl"
                data-testid="mobile-phone-btn"
              >
                <Phone className="h-4 w-4" />
                +40 748 035 510
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
